import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Mail, Shield, ArrowUpDown, Smartphone, LogOut } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export interface User {
  id: number;
  name: string;
  email: string | null;
  username: string;
  role: 'admin' | 'operator';
  isActive: boolean;
  requirePasswordChange?: boolean;
  roles?: string[];
  createdAt?: string;
  updatedAt?: string;
  lastActiveAt?: string | null;
}

export interface UserColumnHandlers {
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onResendInvite: (user: User) => void;
  onDeviceHealth: (user: User) => void;
  onRevokeSessions: (user: User) => void;
}

export function createUserColumns(
  handlers: UserColumnHandlers
): ColumnDef<User>[] {
  const { onEdit, onDelete, onResendInvite, onDeviceHealth, onRevokeSessions } = handlers;

  return [
    // Name column
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    // Email column
    {
      accessorKey: 'email',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Email
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="text-sm">{row.original.email || 'No email'}</span>
      ),
    },
    // Role column
    {
      accessorKey: 'role',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Role
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="space-y-1">
            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
              {user.role === 'admin' ? (
                <>
                  <Shield className="h-3 w-3 mr-1" />
                  Admin
                </>
              ) : (
                'Operator'
              )}
            </Badge>
            {/* Show roles array for debugging */}
            {user.roles && Array.isArray(user.roles) && (
              <div className="text-xs text-gray-500">
                Active: {user.roles.join(', ')}
              </div>
            )}
          </div>
        );
      },
    },
    // Status column
    {
      accessorKey: 'isActive',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        if (user.isActive) {
          return <Badge variant="success">Active</Badge>;
        }
        return <Badge variant="destructive">Archived</Badge>;
      },
    },
    // Last Active column
    {
      accessorKey: 'lastActiveAt',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Last Active
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const lastActiveAt = row.original.lastActiveAt;
        if (!lastActiveAt) {
          return <span className="text-sm text-gray-400">Never</span>;
        }
        const date = new Date(lastActiveAt);
        return (
          <span className="text-sm text-gray-600" title={date.toLocaleString()}>
            {formatDistanceToNow(date, { addSuffix: true })}
          </span>
        );
      },
      sortingFn: (a, b) => {
        const aVal = a.original.lastActiveAt ? new Date(a.original.lastActiveAt).getTime() : 0;
        const bVal = b.original.lastActiveAt ? new Date(b.original.lastActiveAt).getTime() : 0;
        return aVal - bVal;
      },
    },
    // Actions column
    {
      id: 'actions',
      header: () => <div className="text-left">Actions</div>,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(user)}
              title="Edit user"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResendInvite(user)}
              title="Send or resend invitation email"
            >
              <Mail className="h-3 w-3" />
            </Button>
            {user.role === 'operator' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeviceHealth(user)}
                title="Operator progress & phone delivery"
              >
                <Smartphone className="h-3 w-3" />
              </Button>
            )}
            {user.isActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRevokeSessions(user)}
                title="Sign out devices"
              >
                <LogOut className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(user)}
              title="Delete user"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        );
      },
      enableSorting: false,
    },
  ];
}
